/// <reference types="vite-plugin-svgr/client" />
import { useMemo, useState } from 'react'
import { Box, Flex } from '@mantine/core'
import type { DatesRangeValue } from '@mantine/dates'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { DateRangePicker } from '@/components/DateRangePicker/DateRangePicker'
import { Loader } from '@/components/Loader/Loader'
import { Text } from '@/components/Text/Text'
import { Title } from '@/components/Title/Title'

import {
  useGoalsServiceGoalsControllerGetSavedValue,
  useGoalsServiceGoalsControllerGetSavedValueKey,
} from '@/__generated__/queries'

import { formatMoney } from '@/helpers/formatMoney.helper'

import BookmarkIcon from '@/assets/icons/bookmark.svg?react'

import classes from './SavedSummary.module.css'

export function SavedSummary() {
  const [dateRange, setDateRange] = useState<DatesRangeValue>([
    dayjs().subtract(1, 'month').toDate(),
    dayjs().toDate(),
  ])

  const queryClient = useQueryClient()

  const { data, isFetching, isLoading } =
    useGoalsServiceGoalsControllerGetSavedValue({
      endDate: dayjs(dateRange[1]).endOf('day').toISOString(),
      startDate: dayjs(dateRange[0]).endOf('day').toISOString(),
    })

  const savedValue = useMemo<number>(() => data?.savedValue ?? 0, [data])

  const handleDateRangeChange = (value: DatesRangeValue) => {
    setDateRange(value)

    queryClient.invalidateQueries({
      queryKey: [useGoalsServiceGoalsControllerGetSavedValueKey],
    })
  }

  if (isLoading || isFetching) {
    return (
      <Box className={classes.container} h={160} w={{ base: '100%', sm: 360 }}>
        <Loader isMinimized />
      </Box>
    )
  }

  return (
    <Box className={classes.container} w={{ base: '100%', sm: 360 }}>
      <Flex justify="space-between" mb={24}>
        <Flex align="center" gap={16}>
          <BookmarkIcon className={classes.icon} />

          <Title variant="h1_regular">Saved</Title>
        </Flex>

        <Title variant="h1_big_green">{formatMoney(savedValue)}</Title>
      </Flex>
      <Flex direction="column" gap={4} maw={260}>
        <Text variant="body_2">Date range:</Text>
        <DateRangePicker dates={dateRange} onChange={handleDateRangeChange} />
      </Flex>
    </Box>
  )
}
